import { useCallback, useMemo, useState } from 'react';
import type {
  DataTableColumn,
  DataTableRenderCellParams,
  DataTableRowEventHandler
} from '@/components/custom/erp/data-table-v2';
import { ContextMenu, ContextMenuBuilder, useInputTableActions } from '@/components/custom/erp/input-table-v2';
import type { RepeatRowsPayload, ButtonType } from '@/components/custom/erp/input-table-v2';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { useStableEventCallback } from '@/hooks/use-stable-event-callback';
import { SelectedCellInfo, useDetailPaste } from '../../../hooks';
import type { AccountConstraint } from '@/types/schemas';
import { copyCellValue } from '@/lib/table-handlers';
import { getDetailTableColumns } from './columns';
import { useCRUD, useTaxRate } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<BanHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER: ButtonType[] = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'];
const detail = (row: BanHangSearchParams) => constraintValues(row);

export function DetailTab({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  onRepeatForRows,
  constraints = []
}: DetailTabProps) {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: BanHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });

  const { taxRates } = useTaxRate();
  const { fetchDetail } = useCRUD({
    endpoint: QUERY_KEYS.VAT_TU,
    initialFetch: false
  });

  const stableCellValueChange = useStableEventCallback(onCellValueChange);
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });

  const columns = useMemo(
    () => getDetailTableColumns(formMode, taxRates, stableCellValueChange, fetchDetail, cellError),
    [cellError, fetchDetail, formMode, stableCellValueChange, taxRates]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );

  const handleRowId = useCallback((row: BanHangRecord) => row?.uuid || '', []);

  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams<BanHangRecord>) => {
      onCellClick?.(params);
      const pointerEvent = event as MouseEvent;

      setContextMenu({
        position: { x: pointerEvent.clientX, y: pointerEvent.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );

  const tableActions = useInputTableActions({
    onAddRow,
    onCopyRow,
    onDeleteRow,
    onMoveRow,
    onPasteRow
  });

  return (
    <>
      <ErpInputTableV2
        data-perf-id='pn-ban-tra-lai-form-detail-table'
        data-row-count={rows.length}
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={onRowClick as BanHangRecord}
        onCellClick={onCellClick as BanHangRecord}
        onCellContextMenu={handleCellContextMenu}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns as BanHangRecord[]}
        getRowId={handleRowId}
        pasteTarget={selectedCell || undefined}
        actionsDisabled={formMode === 'view'}
        actionOrder={DETAIL_ACTION_ORDER}
        actions={tableActions}
      />

      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
}

export default DetailTab;
